import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';

import type { ColumnConfig } from '@/components/views/ColumnSelector';

const GET_FIELD_SELECTIONS = gql`
  query GetFieldSelections($model: String!, $fieldName: String!) {
    getFieldSelections(model: $model, fieldName: $fieldName) {
      id
      name
      value
      sequence
    }
  }
`;

interface FieldSelectionNode {
  id: string;
  name: string;
  sequence: number;
  value: string;
}

interface GetFieldSelectionsData {
  getFieldSelections: FieldSelectionNode[];
}

/**
 * Hook to fetch and cache selection field labels for multiple columns
 * Returns a map of field -> (value -> label) for efficient lookups
 */
export function useColumnSelectionLabels(
  model: string,
  columns: ColumnConfig[]
): Map<string, Map<string, string>> {
  const [labelMaps, setLabelMaps] = useState<Map<string, Map<string, string>>>(
    new Map()
  );
  const [fetchedFields, setFetchedFields] = useState<Set<string>>(new Set());

  // Identify selection fields that haven't been fetched yet
  const selectionFields = useMemo(() => {
    return columns
      .filter(col => col.type === 'selection')
      .map(col => col.field)
      .filter(field => !fetchedFields.has(field));
  }, [columns, fetchedFields]);

  // Map model names to Odoo model names
  const odooModel = model === 'account' ? 'opstream.account' : 'res.partner';

  // Fetch the first pending selection field
  const currentField = selectionFields[0];

  const { data } = useQuery<GetFieldSelectionsData>(GET_FIELD_SELECTIONS, {
    fetchPolicy: 'cache-first',
    skip: !currentField,
    variables: { fieldName: currentField, model: odooModel }
  });

  // Process the fetched data
  useEffect(() => {
    if (data?.getFieldSelections && currentField) {
      const fieldLabelMap = new Map<string, string>();

      for (const item of data.getFieldSelections) {
        fieldLabelMap.set(item.value, item.name);
      }

      setLabelMaps(prev => {
        const updated = new Map(prev);
        updated.set(currentField, fieldLabelMap);
        return updated;
      });

      setFetchedFields(prev => new Set([currentField, ...prev]));
    }
  }, [data, currentField]);

  return labelMaps;
}
